import React from 'react';
import { motion } from 'framer-motion';

/**
 * ProductMockup — branded, case-contextual UI mockup visuals as inline SVG.
 * Each variant renders a device-framed illustration whose content nods to the
 * client's category (beauty, superapp, optical, outdoor, internal). No stock
 * imagery; every stroke is on-palette.
 */

const palette = {
  ink: '#1A1A1A',
  cream: '#FBFAF6',
  creamSunk: '#F4F2EC',
  forest: '#0F3D22',
  green: '#22A94F',
  greenLight: '#7ED4A0',
  greenDeep: '#178838',
  terracotta: '#C4633F',
  agent: '#6E93B8',
  governance: '#5B4FCF',
};

// ---------- Sephora — mobile PDP-style beauty commerce ----------
function BeautyMock({ accent = palette.green, kicker = 'BEAUTY PDP' }) {
  return (
    <svg viewBox="0 0 480 320" className="w-full h-full" role="img" aria-label="Beauty PDP mockup">
      {/* Panel */}
      <rect x="0" y="0" width="480" height="320" rx="18" fill={palette.cream} />
      {/* Left device */}
      <g transform="translate(30 26)">
        <rect x="0" y="0" width="180" height="268" rx="24" fill={palette.ink} />
        <rect x="8" y="12" width="164" height="244" rx="16" fill={palette.creamSunk} />
        <rect x="24" y="28" width="132" height="132" rx="12" fill={accent} opacity="0.14" />
        {/* Bottle silhouette */}
        <g transform="translate(60 44)">
          <rect x="12" y="12" width="36" height="70" rx="6" fill={accent} />
          <rect x="20" y="4" width="20" height="12" rx="3" fill={palette.ink} />
          <circle cx="30" cy="94" r="4" fill={palette.ink} opacity="0.35" />
        </g>
        <rect x="24" y="176" width="90" height="8" rx="4" fill={palette.ink} opacity="0.85" />
        <rect x="24" y="192" width="62" height="6" rx="3" fill={palette.ink} opacity="0.35" />
        <rect x="24" y="212" width="132" height="28" rx="14" fill={palette.ink} />
        <rect x="34" y="222" width="72" height="8" rx="4" fill={palette.cream} />
      </g>
      {/* Right column — cards */}
      <g transform="translate(228 26)">
        <rect x="0" y="0" width="220" height="80" rx="12" fill={palette.forest} />
        <rect x="16" y="18" width="76" height="8" rx="4" fill={palette.greenLight} />
        <rect x="16" y="34" width="152" height="10" rx="5" fill={palette.cream} />
        <rect x="16" y="52" width="118" height="6" rx="3" fill={palette.cream} opacity="0.55" />

        <rect x="0" y="94" width="220" height="80" rx="12" fill={palette.cream} stroke={palette.creamSunk} />
        <rect x="16" y="112" width="52" height="8" rx="4" fill={accent} />
        <rect x="16" y="128" width="152" height="10" rx="5" fill={palette.ink} />
        <rect x="16" y="146" width="120" height="6" rx="3" fill={palette.ink} opacity="0.4" />

        <rect x="0" y="188" width="220" height="80" rx="12" fill={palette.ink} />
        <rect x="16" y="206" width="52" height="8" rx="4" fill={accent} />
        <rect x="16" y="222" width="132" height="10" rx="5" fill={palette.cream} />
        <rect x="16" y="240" width="94" height="6" rx="3" fill={palette.cream} opacity="0.55" />
      </g>
      {/* kicker */}
      <text x="30" y="308" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.ink} opacity="0.55" letterSpacing="2">{kicker}</text>
    </svg>
  );
}

// ---------- Al-Futtaim — SuperApp home w/ multi-brand tiles ----------
function SuperAppMock({ accent = palette.governance, kicker = 'SUPERAPP HOME' }) {
  return (
    <svg viewBox="0 0 480 320" className="w-full h-full" role="img" aria-label="SuperApp mockup">
      <rect x="0" y="0" width="480" height="320" rx="18" fill={palette.cream} />
      {/* Device */}
      <g transform="translate(30 22)">
        <rect x="0" y="0" width="180" height="276" rx="24" fill={palette.ink} />
        <rect x="8" y="12" width="164" height="252" rx="16" fill={palette.creamSunk} />
        {/* Top hero */}
        <rect x="20" y="26" width="140" height="60" rx="10" fill={palette.forest} />
        <rect x="30" y="40" width="60" height="8" rx="4" fill={palette.greenLight} />
        <rect x="30" y="54" width="100" height="10" rx="5" fill={palette.cream} />
        <rect x="30" y="70" width="70" height="6" rx="3" fill={palette.cream} opacity="0.55" />
        {/* Brand tiles */}
        {[0,1,2,3,4,5].map((i) => (
          <g key={i} transform={`translate(${20 + (i%3)*48} ${100 + Math.floor(i/3)*54})`}>
            <rect width="40" height="46" rx="8" fill={i%3===0?accent:i%3===1?palette.terracotta:palette.green} opacity={0.85} />
            <rect x="8" y="30" width="24" height="6" rx="3" fill={palette.cream} opacity="0.9" />
          </g>
        ))}
      </g>
      {/* Right — data / connections */}
      <g transform="translate(230 40)">
        <text x="0" y="10" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={accent} letterSpacing="2">CROSS-BRAND GRAPH</text>
        {/* nodes */}
        {[[24,52],[110,32],[190,60],[70,112],[164,132],[30,180],[130,192]].map(([x,y],i) => (
          <g key={i}>
            <circle cx={x} cy={y} r={i%2?7:5} fill={i%2?accent:palette.green} />
          </g>
        ))}
        {/* edges */}
        <path d="M24 52 L70 112 L110 32 M110 32 L190 60 L164 132 L70 112 M30 180 L70 112 M130 192 L164 132" stroke={palette.ink} strokeOpacity="0.35" strokeWidth="1" fill="none" />
        {/* labels */}
        <rect x="0" y="216" width="220" height="34" rx="8" fill={palette.forest} />
        <text x="14" y="238" fontFamily="Inter" fontSize="11" fill={palette.greenLight}>200+ companies, one experience</text>
      </g>
      <text x="30" y="308" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.ink} opacity="0.55" letterSpacing="2">{kicker}</text>
    </svg>
  );
}

// ---------- MAGRABi — omnichannel eye-care ----------
function OpticalMock({ accent = palette.terracotta, kicker = 'OMNICHANNEL BOOK' }) {
  return (
    <svg viewBox="0 0 480 320" className="w-full h-full" role="img" aria-label="Optical mockup">
      <rect x="0" y="0" width="480" height="320" rx="18" fill={palette.cream} />
      {/* Web tablet frame */}
      <g transform="translate(24 30)">
        <rect x="0" y="0" width="260" height="260" rx="14" fill={palette.ink} />
        <rect x="10" y="10" width="240" height="240" rx="8" fill={palette.creamSunk} />
        <rect x="24" y="24" width="120" height="8" rx="4" fill={palette.ink} />
        <rect x="24" y="40" width="76" height="6" rx="3" fill={palette.ink} opacity="0.4" />
        {/* glasses grid */}
        {[0,1,2,3].map((i) => (
          <g key={i} transform={`translate(${24 + (i%2)*112} ${68 + Math.floor(i/2)*90})`}>
            <rect width="100" height="76" rx="10" fill={palette.cream} stroke={palette.creamSunk} />
            {/* frames */}
            <g transform="translate(14 22)">
              <ellipse cx="14" cy="16" rx="14" ry="10" fill="none" stroke={i%2?accent:palette.ink} strokeWidth="2" />
              <ellipse cx="58" cy="16" rx="14" ry="10" fill="none" stroke={i%2?accent:palette.ink} strokeWidth="2" />
              <path d="M28 16 L44 16" stroke={i%2?accent:palette.ink} strokeWidth="2" />
            </g>
            <rect x="14" y="60" width="34" height="6" rx="3" fill={i%2?accent:palette.green} />
          </g>
        ))}
      </g>
      {/* Right — appointment slot */}
      <g transform="translate(300 30)">
        <rect x="0" y="0" width="156" height="120" rx="12" fill={palette.forest} />
        <text x="14" y="26" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.greenLight} letterSpacing="2">BOOK EYE TEST</text>
        <rect x="14" y="40" width="128" height="8" rx="4" fill={palette.cream} />
        <rect x="14" y="54" width="90" height="6" rx="3" fill={palette.cream} opacity="0.55" />
        {['09:00','10:30','12:00','14:00'].map((t,i) => (
          <g key={t} transform={`translate(${14 + (i%2)*66} ${72 + Math.floor(i/2)*22})`}>
            <rect width="58" height="18" rx="9" fill={i===1?accent:palette.cream} opacity={i===1?1:0.15} />
            <text x="10" y="12" fontFamily="Inter" fontSize="10" fill={i===1?palette.ink:palette.cream}>{t}</text>
          </g>
        ))}
        <rect x="0" y="132" width="156" height="80" rx="12" fill={palette.cream} stroke={palette.creamSunk} />
        <text x="14" y="152" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={accent} letterSpacing="2">IN-STORE PICKUP</text>
        <rect x="14" y="162" width="128" height="8" rx="4" fill={palette.ink} />
        <rect x="14" y="176" width="80" height="6" rx="3" fill={palette.ink} opacity="0.4" />
        <rect x="14" y="192" width="60" height="12" rx="6" fill={accent} />
      </g>
      <text x="24" y="308" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.ink} opacity="0.55" letterSpacing="2">{kicker}</text>
    </svg>
  );
}

// ---------- Fenix Outdoor — multi-brand storefront + regional map ----------
function OutdoorMock({ accent = palette.greenDeep, kicker = 'MULTI-BRAND · EU · US' }) {
  return (
    <svg viewBox="0 0 480 320" className="w-full h-full" role="img" aria-label="Outdoor multi-brand mockup">
      <rect x="0" y="0" width="480" height="320" rx="18" fill={palette.cream} />
      {/* Browser frame */}
      <g transform="translate(24 26)">
        <rect x="0" y="0" width="432" height="268" rx="12" fill={palette.ink} />
        <rect x="0" y="0" width="432" height="26" fill={palette.ink} />
        <circle cx="14" cy="13" r="3.5" fill="#FF6B57" />
        <circle cx="28" cy="13" r="3.5" fill="#F5C24C" />
        <circle cx="42" cy="13" r="3.5" fill={accent} />
        <rect x="60" y="6" width="360" height="14" rx="7" fill={palette.creamSunk} opacity="0.9" />
        <rect x="0" y="26" width="432" height="242" fill={palette.creamSunk} />
        {/* Hero */}
        <rect x="18" y="42" width="240" height="140" rx="10" fill={palette.forest} />
        {/* mountains */}
        <path d="M18 182 L70 110 L110 150 L160 88 L220 160 L258 128 L258 182 Z" fill={accent} />
        <path d="M18 182 L54 140 L86 168 L136 118 L178 172 L220 148 L258 178 L258 182 Z" fill={palette.greenLight} opacity="0.55" />
        <rect x="34" y="58" width="60" height="8" rx="4" fill={palette.greenLight} />
        <rect x="34" y="74" width="118" height="10" rx="5" fill={palette.cream} />
        {/* Brand chips */}
        {[0,1,2,3,4,5,6,7,8,9].map((i) => (
          <g key={i} transform={`translate(${272 + (i%2)*76} ${42 + Math.floor(i/2)*28})`}>
            <rect width="66" height="22" rx="6" fill={i%3===0?accent:i%3===1?palette.terracotta:palette.ink} opacity={0.85} />
            <rect x="10" y="8" width="46" height="6" rx="3" fill={palette.cream} opacity="0.85" />
          </g>
        ))}
        {/* Bottom row */}
        <rect x="18" y="200" width="132" height="52" rx="10" fill={palette.cream} stroke={palette.creamSunk} />
        <rect x="30" y="212" width="60" height="8" rx="4" fill={accent} />
        <rect x="30" y="226" width="90" height="6" rx="3" fill={palette.ink} opacity="0.55" />
        <rect x="30" y="238" width="60" height="8" rx="4" fill={palette.ink} />
        <rect x="162" y="200" width="132" height="52" rx="10" fill={palette.cream} stroke={palette.creamSunk} />
        <rect x="174" y="212" width="60" height="8" rx="4" fill={accent} />
        <rect x="174" y="226" width="90" height="6" rx="3" fill={palette.ink} opacity="0.55" />
        <rect x="174" y="238" width="60" height="8" rx="4" fill={palette.ink} />
        <rect x="306" y="200" width="108" height="52" rx="10" fill={palette.forest} />
        <rect x="318" y="212" width="60" height="8" rx="4" fill={palette.greenLight} />
        <rect x="318" y="226" width="80" height="6" rx="3" fill={palette.cream} opacity="0.55" />
      </g>
      <text x="24" y="308" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.ink} opacity="0.55" letterSpacing="2">{kicker}</text>
    </svg>
  );
}

// ---------- DigitalWorks internal — CI/ledger dashboard ----------
function LedgerMock({ accent = palette.governance, kicker = 'AGENT EXECUTION LEDGER' }) {
  return (
    <svg viewBox="0 0 480 320" className="w-full h-full" role="img" aria-label="Ledger mockup">
      <rect x="0" y="0" width="480" height="320" rx="18" fill={palette.ink} />
      {/* Sidebar */}
      <rect x="0" y="0" width="90" height="320" rx="18" fill={palette.forest} />
      {['Runs','Diffs','Gates','Ledger','Evals'].map((l,i) => (
        <g key={l} transform={`translate(14 ${44 + i*32})`}>
          <rect width="66" height="22" rx="6" fill={i===2?accent:palette.greenLight} opacity={i===2?1:0.15} />
          <text x="10" y="14" fontFamily="Inter" fontSize="10" fill={i===2?palette.cream:palette.greenLight}>{l}</text>
        </g>
      ))}
      {/* Main panel */}
      <g transform="translate(110 22)">
        <text x="0" y="12" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.greenLight} letterSpacing="2">TIER 3 · WAITING FOR APPROVAL</text>
        <rect x="0" y="20" width="340" height="10" rx="5" fill={palette.cream} opacity="0.95" />
        <rect x="0" y="34" width="240" height="8" rx="4" fill={palette.cream} opacity="0.55" />
        {/* Diff block */}
        <rect x="0" y="56" width="340" height="152" rx="10" fill="#0B1E14" />
        {[
          { c: palette.terracotta, w: 200 },
          { c: palette.greenLight, w: 240 },
          { c: palette.greenLight, w: 180 },
          { c: palette.cream, w: 260, o: 0.4 },
          { c: palette.terracotta, w: 160 },
          { c: palette.greenLight, w: 220 },
          { c: palette.cream, w: 200, o: 0.3 },
          { c: palette.greenLight, w: 260 },
        ].map((row, i) => (
          <g key={i} transform={`translate(14 ${72 + i*16})`}>
            <text x="0" y="0" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.cream} opacity="0.4">{String(i+1).padStart(2,'0')}</text>
            <rect x="22" y="-8" width={row.w} height="8" rx="4" fill={row.c} opacity={row.o ?? 0.85} />
          </g>
        ))}
        {/* Approve/reject */}
        <rect x="0" y="222" width="120" height="34" rx="17" fill={accent} />
        <text x="16" y="243" fontFamily="Inter" fontSize="12" fill={palette.cream} fontWeight="600">APPROVE · P2</text>
        <rect x="132" y="222" width="120" height="34" rx="17" fill="none" stroke={palette.cream} strokeOpacity="0.35" />
        <text x="150" y="243" fontFamily="Inter" fontSize="12" fill={palette.cream} opacity="0.65">SEND BACK</text>
      </g>
      <text x="110" y="304" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={palette.greenLight} opacity="0.7" letterSpacing="2">{kicker}</text>
    </svg>
  );
}

const VARIANTS = {
  beauty: BeautyMock,
  superapp: SuperAppMock,
  optical: OpticalMock,
  outdoor: OutdoorMock,
  ledger: LedgerMock,
};

export default function ProductMockup({ variant = 'beauty', kicker, accent, animate = true, className = '' }) {
  const V = VARIANTS[variant] || BeautyMock;
  const content = (
    <div className={`w-full h-full ${className}`}>
      <V kicker={kicker} accent={accent} />
    </div>
  );
  if (!animate) return content;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`w-full h-full ${className}`}
    >
      <V kicker={kicker} accent={accent} />
    </motion.div>
  );
}

export { palette as mockupPalette };
